// src/pages/componenets/SectionSelector.jsx
import React from "react";
import { sectionTemplates } from "../../data/database";

const resolveSectionTypeKey = (rawType, templatesObj) => {
  const t = (rawType || "").trim();
  if (!t) return t;

  const lower = t.toLowerCase();

  const candidates = [
    t,
    lower,

    // category
    t === "categorySection" ? "categorysection" : null,
    t === "categorysection" ? "categorySection" : null,

    // location
    t === "locationSection" ? "locationsection" : null,
    t === "locationsection" ? "locationSection" : null,

    t === "serviceSection" ? "servicesection" : null,
    t === "servicesection" ? "serviceSection" : null,

  ].filter(Boolean);

  return candidates.find((k) => templatesObj?.[k]) || t;
};

// For labels/icons we treat categorysection/locationsection as camelCase labels
const toLabelKey = (typeKey) => {
  const lower = (typeKey || "").toLowerCase();
  if (lower === "categorysection") return "categorySection";
  if (lower === "locationsection") return "locationSection";
  if (lower === "servicesection") return "serviceSection";

  return typeKey;
};

const SECTION_LABELS = {
  hero: "Hero",
  features: "Features",
  testimonials: "Testimonials",
  gallery: "Gallery",
  cta: "CTA",
  categorySection: "Category Section",
  locationSection: "Location Section",
  serviceSection: "Service Section",

};

const SectionSelector = ({ sectionType, onSelectTemplate, currentTemplate, onClose }) => {
  const resolvedType = resolveSectionTypeKey(sectionType, sectionTemplates);
  const labelKey = toLabelKey(resolvedType);

  const templates = sectionTemplates?.[resolvedType] || {};
  const templateArray = Object.entries(templates).map(([templateKey, tpl]) => ({
    templateKey,
    ...tpl,
  }));

  const sectionLabel =
    SECTION_LABELS[labelKey] ||
    (labelKey ? labelKey.charAt(0).toUpperCase() + labelKey.slice(1) : "Section");

  const getSectionIcon = () => {
    switch (labelKey) {
      case "hero":
        return "🚀";
      case "features":
        return "⚡";
      case "testimonials":
        return "💬";
      case "gallery":
        return "🖼️";
      case "cta":
        return "🎯";
      case "categorySection":
        return "🛍️";
      case "locationSection":
        return "📍";

      case "serviceSection":
        return "🧩";
      default:
        return "📄";
    }
  };

  const getPreviewColor = () => {
    switch (labelKey) {
      case "hero":
        return "from-blue-100 to-blue-200";
      case "features":
        return "from-green-100 to-green-200";
      case "testimonials":
        return "from-purple-100 to-purple-200";
      case "gallery":
        return "from-pink-100 to-pink-200";
      case "cta":
        return "from-orange-100 to-orange-200";
      case "categorySection":
        return "from-teal-100 to-teal-200";
      case "locationSection":
        return "from-indigo-100 to-indigo-200";
      case "serviceSection":
        return "from-sky-100 to-sky-200";
      default:
        return "from-gray-100 to-gray-200";
    }
  };

  const renderGalleryCountLine = (template) => {
    if (labelKey !== "gallery") return null;

    const projects = Array.isArray(template?.config?.projects) ? template.config.projects : null;
    const featured = Array.isArray(template?.config?.featuredProjects) ? template.config.featuredProjects : null;

    if (projects) return <div className="text-xs text-gray-500">{projects.length} projects</div>;
    if (featured) return <div className="text-xs text-gray-500">{featured.length} featured projects</div>;
    return null;
  };

  const renderTestimonialsCountLine = (template) => {
    if (labelKey !== "testimonials") return null;
    const list = Array.isArray(template?.config?.testimonials) ? template.config.testimonials : [];
    return <div className="text-xs text-gray-500">{list.length} testimonials</div>;
  };

  const renderLocationsCountLine = (template) => {
    if (labelKey !== "locationSection") return null;

    // template1 uses single address fields, template2 uses config.locations array
    const locations = Array.isArray(template?.config?.locations) ? template.config.locations : null;
    if (locations) return <div className="text-xs text-gray-500">{locations.length} locations</div>;

    return <div className="text-xs text-gray-500">Single location</div>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="border-b border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPreviewColor()} flex items-center justify-center`}>
                <span className="text-2xl">{getSectionIcon()}</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Select {sectionLabel} Template</h2>
                <p className="text-gray-600 mt-2">Choose from our professionally designed templates</p>
              </div>
            </div>

            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 120px)" }}>
          {templateArray.length === 0 ? (
            <div className="p-6 border border-dashed border-gray-300 rounded-xl text-center text-gray-600">
              No templates found for "{resolvedType}".
              <div className="text-xs text-gray-500 mt-2">
                Check that sectionTemplates has this key and that your section type matches it.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {templateArray.map((template) => {
                const isActive = currentTemplate === template.templateKey;

                return (
                  <div
                    key={template.templateKey}
                    className={`border-2 rounded-xl overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-xl ${
                      isActive ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200 hover:border-blue-300"
                    }`}
                    onClick={() => onSelectTemplate(template.templateKey)}
                  >
                    <div className={`h-48 bg-gradient-to-br ${getPreviewColor()} flex items-center justify-center`}>
                      <div className="text-center">
                        <div className="text-4xl mb-3">{getSectionIcon()}</div>
                        <div className="text-sm font-medium text-gray-700">{template.name || template.templateKey}</div>
                        <div className="text-xs text-gray-600 mt-2">{sectionLabel} template</div>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-bold text-gray-900">{template.name || template.templateKey}</h3>
                        {isActive && (
                          <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">Active</span>
                        )}
                      </div>

                      <div className="space-y-2 mb-4">
                        {template.config?.title && (
                          <div className="text-sm text-gray-600 truncate">
                            <span className="font-medium">Title:</span> {template.config.title}
                          </div>
                        )}
                        {template.config?.subtitle && (
                          <div className="text-sm text-gray-600 truncate">
                            <span className="font-medium">Subtitle:</span> {template.config.subtitle}
                          </div>
                        )}
                        

                        {labelKey === "locationSection" && (
                            <div className="text-xs text-gray-500">
                              {Array.isArray(template.config?.locations) ? `${template.config.locations.length} locations` : "Location section"}
                            </div>
                          )}
                        {labelKey === "serviceSection" && (
                          <div className="text-xs text-gray-500">
                            {Array.isArray(template.config?.services) ? `${template.config.services.length} services` : "Service section"}
                          </div>
                        )}

                        {renderTestimonialsCountLine(template)}
                        {renderLocationsCountLine(template)}
                        {renderGalleryCountLine(template)}
                      </div>

                      <div className="flex items-center justify-between">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectTemplate(template.templateKey);
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {isActive ? "Selected ✓" : "Select Template"}
                        </button>

                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">{template.templateKey}</span>
                          <div className={`w-2 h-2 rounded-full ${isActive ? "bg-blue-500" : "bg-gray-300"}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="font-medium text-gray-900 mb-4">About {sectionLabel} Sections</h3>
            <div className="text-sm text-gray-600 space-y-2">
              {labelKey === "hero" && <p>The hero section is the first thing visitors see.</p>}
              {labelKey === "features" && <p>Showcase your key features and benefits.</p>}
              {labelKey === "testimonials" && <p>Build trust with social proof.</p>}
              {labelKey === "gallery" && <p>Display your portfolio or highlights visually.</p>}
              {labelKey === "cta" && <p>Encourage visitors to take the next step.</p>}
              {labelKey === "categorySection" && <p>Show categories in a clean and clickable layout.</p>}
              {labelKey === "locationSection" && <p>Show your address, map, branches, and contact info.</p>}
              {labelKey === "serviceSection" && <p>Present your services, packages, or pricing in multiple layouts.</p>}

              <p className="text-xs text-gray-500 mt-4">Tip: Click on any template to preview it live</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">{templateArray.length} templates available</div>
            <div className="flex space-x-4">
              <button
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionSelector;
